import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";

/** `prompt`, in the terminal register — a `❯` sigil and the text, flush left.
 *
 * The native skin right-aligns the prompt and calls alignment "the shape",
 * because a reader scanning a long web transcript finds turn boundaries down
 * the right edge. A terminal cannot borrow that: every row starts at column
 * zero, and a right-aligned line in a monospace column reads as damage, not
 * as emphasis. The sigil does the same job in the register that has one —
 * it is the same thing a shell does, and for the same reason.
 *
 * So the scanning property survives while the mechanism does not, which is
 * the pattern this whole skin keeps finding: the contract carries what a turn
 * IS, and each skin owns how it is found. */
@component({ name: "std-term-turn-prompt" })
export class TermTurnPrompt extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");

  static makeWith(props: TurnSlots.Props["prompt"]): TermTurnPrompt {
    return (TermTurnPrompt.make() as TermTurnPrompt).bind(props);
  }

  bind(props: TurnSlots.Props["prompt"]): this {
    this.$text.textContent = props.text;
    return this;
  }

  static $styles = [
    css`
      :host {
        display: flex;
        align-items: baseline;
        gap: 1ch;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
      }
      #sigil {
        flex: none;
        width: 1ch;
        color: var(--fg-green, #2da44e);
      }
      #text {
        margin: 0;
        color: var(--text-pri, #1f2328);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <span id="sigil">❯</span>
    <p id="text"></p>
  `;
}
